import get from 'lodash/get';
import pick from 'lodash/pick';
import isString from 'lodash/isString';
import {useState} from 'react';
import {useNavigate,useParams,useSearchParams} from 'react-router-dom';

import {
  useGetPatientsQuery,
  useUpdatePatientMutation
} from '../../redux/services/patientApi';
import * as validations from '../../helpers/validations';
import {useGetFilesQuery} from '../../redux/services/fileApi';
import {formatDateOfBirth} from '../../helpers/patients';
import getPronouns from '../../helpers/getPronouns';
import {getReferralSourceCategories, getRelations} from '../../helpers/selectOptions';
import {useGetSessionsQuery} from '../../redux/services/sessionApi';
import useUser from '../../hooks/useUser';
import useError from '../../hooks/useError';
import useInterval from '../../hooks/useInterval';
import AppDashboard from '../AppDashboard';
import ViewPatientScreen from '../../components/patients/ViewPatientScreen';

const sections = [
  {key: 'basics', title: 'Basic Information', isForm: true, isMedical: false},
  {key: 'address', title: 'Address', isForm: true,  isMedical: false},
  {key: 'emergency', title: 'Emergency Contact', isForm: true,  isMedical: false},
  {key: 'guardian', title: 'Guardian', isForm: true,  isMedical: false},
  {key: 'primaryCare', title: 'Referring Physician', isForm: true, isMedical: true},
  {key: 'medicalIntake', title: 'Medical Intake', isForm: true, isMedical: true},
  {key: 'referralInfo', title: 'Referral Details', isForm: true, isMedical: true, hideFromPatient: true},
  {key: 'files', title: 'Medical Documents', isForm: false, isMedical: true},
  {key: 'sessions', title: 'Sessions', isForm: false, isMedical: false},
];

const getFields = p => (
  [
    {
      label: 'First Name',
      key: 'user.first_name',
      type: 'text',
      disabled: true,
      data: get(p, 'user.first_name'),
      section: 'basics',
      optional: false,
      validate: val => isString(val),
    },
    {
      label: 'Last Name',
      key: 'user.last_name',
      type: 'text',
      disabled: true,
      data: get(p, 'user.last_name'),
      section: 'basics',
      optional: false,
      validate: val => isString(val),
    },
    {
      label: 'Date of Birth',
      key: 'date_birth',
      type: 'text',
      disabled: true,
      data: formatDateOfBirth(p),
      section: 'basics',
      optional: false,
      validate: val => isString(val),
    },
    {
      label: 'Email',
      key: 'user.email',
      type: 'text',
      data: get(p, 'user.email', ''),
      section: 'basics',
      optional: false,
      validate: val => validations.email(val),
    },
    {
      label: 'Phone',
      key: 'phone',
      type: 'phone',
      data: get(p, 'phone'),
      section: 'basics',
      optional: false,
      validate: val => true,
    },
    {
      label: 'Sex Assigned at Birth',
      key: 'sex',
      type: 'select',
      helperText: 'This is primarily used for insurance billing',
      data: get(p, 'sex'),
      section: 'basics',
      optional: true,
      validate: val => !!val,
      options: [
        {label: 'Female', value: 'F'},
        {label: 'Male', value: 'M'},
      ],
    },
    {
      label: 'Pronouns',
      key: 'personal_pronouns',
      type: 'select',
      data: get(p, 'personal_pronouns'),
      section: 'basics',
      optional: true,
      validate: val => true,
      options: getPronouns(),
    },
    {
      label: 'Address',
      key: 'address',
      type: 'address',
      data: pick(p, 'address_line_1', 'address_line_2', 'address_city', 'address_state', 'address_zip'),
      section: 'address',
      optional: false,
      validate: () => true,
    },
    {
      label: 'Full Name',
      key: 'primary_care_doctor_name',
      type: 'text',
      data: get(p, 'primary_care_doctor_name', ''),
      section: 'primaryCare',
      optional: true,
      validate: val => validations.name(val),
    },
    {
      label: 'Medical Practice',
      key: 'primary_care_doctor_medical_practice',
      type: 'text',
      data: get(p, 'primary_care_doctor_medical_practice', ''),
      section: 'primaryCare',
      optional: true,
      validate: val => isString(val),
    },
    {
      label: 'Phone',
      key: 'primary_care_doctor_phone',
      type: 'phone',
      data: get(p, 'primary_care_doctor_phone', ''),
      section: 'primaryCare',
      optional: true,
      validate: val => validations.phone(val),
    },
    {
      label: 'Fax',
      key: 'primary_care_doctor_fax',
      type: 'phone',
      data: get(p, 'primary_care_doctor_fax', ''),
      section: 'primaryCare',
      optional: true,
      validate: val => validations.phone(val),
    },
    {
      label: 'Full Name',
      key: 'emergency_contact_name',
      type: 'text',
      data: get(p, 'emergency_contact_name', ''),
      section: 'emergency',
      optional: true,
      validate: val => validations.name(val),
    },
    {
      label: 'Relation',
      key: 'emergency_contact_relation',
      type: 'select',
      data: get(p, 'emergency_contact_relation', ''),
      section: 'emergency',
      optional: true,
      options: getRelations(),
      validate: val => true,
    },
    {
      label: 'Phone Number',
      key: 'emergency_contact_phone',
      type: 'phone',
      data: get(p, 'emergency_contact_phone', ''),
      section: 'emergency',
      optional: true,
      validate: val => validations.phone(val),
    },
    {
      label: 'First Name',
      key: 'guardian_first_name',
      type: 'text',
      data: get(p, 'guardian_first_name', ''),
      section: 'guardian',
      optional: true,
      validate: val => val ? validations.name(val) : true,
    },
    {
      label: 'Last Name',
      key: 'guardian_last_name',
      type: 'text',
      data: get(p, 'guardian_last_name', ''),
      section: 'guardian',
      optional: true,
      validate: val => val ? validations.name(val) : true,
    },
    {
      label: 'Phone',
      key: 'guardian_phone',
      type: 'phone',
      data: get(p, 'guardian_phone', ''),
      section: 'guardian',
      optional: true,
      validate: val => val ? validations.phone(val) : true,
    },
    {
      label: 'Email',
      key: 'guardian_email',
      type: 'text',
      data: get(p, 'guardian_email', ''),
      section: 'guardian',
      optional: true,
      validate: val => val ? validations.email(val) : true,
    },
    {
      label: 'Relation',
      key: 'guardian_relation',
      type: 'select',
      data: get(p, 'guardian_relation', ''),
      section: 'guardian',
      optional: true,
      options: getRelations(),
      validate: val => true,
    },
    {
      label: 'Occupation',
      key: 'occupation',
      type: 'text',
      section: 'medicalIntake',
      data: get(p, 'occupation', ''),
      optional: true,
      validate: val => isString(val),
    },
    {
      label: 'Goals',
      key: 'goals',
      type: 'text',
      section: 'medicalIntake',
      data: get(p, 'goals', ''),
      optional: true,
      validate: val => isString(val),
    },
    {
      label: 'Movement During the Day',
      key: 'movement_amount',
      type: 'select',
      section: 'medicalIntake',
      data: get(p, 'movement_amount', ''),
      optional: true,
      options: [
        {label: 'Rarely - I sit most of the day', value: 'rarely'},
        {label: 'Occasionally - every hour or two', value: 'occasionally'},
        {label: 'Often - as frequently as I can', value: 'often'},
        {label: 'Varies - Depends on the day', value: 'varies'},
      ],
      validate: val => isString(val),
    },
    {
      label: 'Patient\'s Outlook',
      key: 'outlook',
      type: 'select',
      section: 'medicalIntake',
      data: get(p, 'outlook', ''),
      optional: true,
      options: [
        {value: 'happy', label: 'I’m often happy'},
        {value: 'medium', label: 'I have my ups and downs, but I manage'},
        {value: 'blue', label: 'I often feel very blue'},
      ],
      validate: val => isString(val),
    },
    {
      label: 'Hours of Sleep',
      key: 'sleep',
      type: 'select',
      section: 'medicalIntake',
      data: get(p, 'sleep', ''),
      optional: true,
      options: [
        {value: '<5', label: '< 5 hours'},
        {value: '5-6', label: '5-6 hours'},
        {value: '7-8', label: '7-8 hours'},
        {value: '>8', label: '> 8 hours'},
      ],
      validate: val => isString(val),
    },
    {
      label: 'Exercise Frequency',
      key: 'exercise',
      type: 'select',
      section: 'medicalIntake',
      data: get(p, 'exercise', ''),
      optional: true,
      options: [
        {value: 'never', label: 'Never'},
        {value: '1-2', label: '1-2 times per week'},
        {value: '3-4', label: '3-4 times per week'},
        {value: '5+', label: '5 or more times a week'},
      ],
      validate: val => isString(val),
    },
    {
      label: 'Rating of Ability to Exercise',
      key: 'ability',
      type: 'select',
      section: 'medicalIntake',
      data: get(p, 'ability', ''),
      optional: true,
      options: [
        {value: 'novice', label: 'Novice, I’ve never really exercised'},
        {value: 'average', label: 'Average, I’ve exercised before but I’m not confident enough to teach someone'},
        {value: 'pro', label: 'I’m a pro, I could teach exercise'},
      ],
      validate: val => isString(val),
    },
    {
      label: 'Rating of Diet and Nutrition',
      key: 'diet',
      type: 'select',
      section: 'medicalIntake',
      data: get(p, 'diet', ''),
      optional: true,
      options: [
        {value: 'novice', label: 'Poor'},
        {value: 'average', label: 'Average'},
        {value: 'better', label: 'Better than most'},
        {value: 'excellent', label: 'Excellent'},
      ],
      validate: val => isString(val),
    },
    {
      label: 'Stress Affecting Health',
      key: 'stress',
      type: 'select',
      section: 'medicalIntake',
      data: get(p, 'stress', ''),
      optional: true,
      options: [
        {value: 'yes', label: 'Yes, definitely'},
        {value: 'little', label: 'A little bit'},
        {value: 'good', label: 'No, I\'m good'},
      ],
      validate: val => isString(val),
    },
    {
      label: 'Pain, Concerning Symptoms, or Dysfunction',
      key: 'pain',
      type: 'text',
      section: 'medicalIntake',
      data: get(p, 'pain', ''),
      optional: true,
      validate: val => isString(val),
    },
    {
      label: 'Family History',
      key: 'family_history',
      type: 'text',
      section: 'medicalIntake',
      data: get(p, 'family_history', ''),
      optional: true,
      validate: val => isString(val),
    },
    {
      label: 'Relevant Surgeries',
      key: 'surgeries',
      type: 'text',
      section: 'medicalIntake',
      data: get(p, 'surgeries', ''),
      optional: true,
      validate: val => isString(val),
    },
    {
      label: 'Medications',
      key: 'medications',
      type: 'text',
      section: 'medicalIntake',
      data: get(p, 'medications', ''),
      optional: true,
      validate: val => isString(val),
    },
    {
      label: 'Details',
      key: 'referral_details',
      type: 'text',
      section: 'referralInfo',
      data: get(p, 'referral_details', ''),
      optional: true,
      validate: val =>isString(val),
    },
    {
      label: 'Category',
      key: 'patient_referral',
      type: 'select',
      section: 'referralInfo',
      data: get(p, 'patient_referral', ''),
      optional: true,
      validate: val => isString(val),
      options: getReferralSourceCategories(),
    },
  ]
);

const handleValidate = ({fields}) => updates => {
  const invalidValues = Object.keys(updates)
    .filter(k => k !== 'address')
    .filter(k => {
      const match = fields.find(f => f.key === k);
      if (!match?.validate) return false;
      return !match.validate(updates[k]);
    })
    .map(k => fields.find(f => f.key === k).label);
  if (invalidValues.length === 0) return {error: null};
  const errors = invalidValues.join(', ');
  return {error: new Error(`The following fields are invalid: ${errors}`)};
};

const handleAutosave = ({
  patientId,
  updatePatient,
  updates,
  setUpdates,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  setError,
  validateFormData,
}) => async () => {
  if (!hasUnsavedChanges) return setError(null);
  if (Object.keys(updates).length === 0) return setHasUnsavedChanges(false);
  const {error} = validateFormData(updates);
  if (error) return setError(error);
  const result = await updatePatient({id: patientId, body: updates});
  if (result.error) {
    const message = get(result, 'error.data.errors[0]', null);
    return setError(new Error(message));
  }
  setUpdates({});
  setHasUnsavedChanges(false);
};

const handleFieldChange = ({updates, setUpdates, setHasUnsavedChanges}) => field => e => {
  let val, newUpdates;
  if (field === 'address') {
    val = get(e, 'target.value', {});
    newUpdates = {...updates, ...val};
  } else {
    val = get(e, 'target.value');
    newUpdates = {...updates, [field]: val};
  }
  setUpdates(newUpdates);
  setHasUnsavedChanges(true);
};

const ViewPatientPage = props => {
  const {id} = useParams();
  const [query] = useSearchParams();
  const excludeAccountInfo = query.get('excludeAccountInfo');
  const excludeMedicalHistory = query.get('excludeMedicalHistory');
  const navigate = useNavigate();
  const [updates, setUpdates] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const user = useUser();
  const patientsQuery = useGetPatientsQuery({id}, {skip: !id});
  const patient = (get(patientsQuery, 'data.data') || []).find(
    p => p.id.toString() === id
  );
  const filesQuery = useGetFilesQuery({patient_id: id}, {skip: !id});
  const files = get(filesQuery, 'data.data', []) || [];
  const sessionsQuery = useGetSessionsQuery({patient_id: id}, {skip: !id});
  const sessions = get(sessionsQuery, 'data.data', []) || [];
  // eslint-disable-next-line no-unused-vars
  const [updatePatient, updateResult] = useUpdatePatientMutation();
  const errors = useError();
  const fields = getFields(patient);
  const autosave = handleAutosave({
    patientId: get(patient, 'id'),
    updates,
    setUpdates,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    updatePatient,
    setError: errors.create,
    validateFormData: handleValidate({fields}),
  });
  useInterval(autosave, {delay: 5000});
  const childProps = {
    user,
    patient,
    files,
    sessions,
    updates,
    hasUnsavedChanges,
    sections: sections.filter(s => {
      if (excludeMedicalHistory && s.isMedical) return false;
      if (excludeAccountInfo && !s.isMedical) return false;
      return true;
    }),
    fields,
    isProvider: user?.is_provider || false,
    loading: patientsQuery.isLoading || !user || !patient,
    onSave: autosave,
    onFieldChange: handleFieldChange({updates, setUpdates, setHasUnsavedChanges}),
  };
  return (
    <AppDashboard
      title='Patient Details'
      loading={childProps.loading}
      hasNoData={!childProps.loading && !childProps.patient}
      onBack={() => navigate('/providers/patients')}
      hideBack={true}
    >
      <ViewPatientScreen {...childProps} />
    </AppDashboard>
  );
};

export default ViewPatientPage;
